import ContentCopyIcon from '@material-design-icons/svg/filled/content_copy.svg?react';
import ExpandLessIcon from '@material-design-icons/svg/filled/expand_less.svg?react';
import ExpandMoreIcon from '@material-design-icons/svg/filled/expand_more.svg?react';
import OpenInNewIcon from '@material-design-icons/svg/filled/open_in_new.svg?react';
import RefreshIcon from '@material-design-icons/svg/filled/refresh.svg?react';
import { useState } from 'react';
import { toast } from 'sonner';
import { useMetadataFetch } from '../hooks/useMetadataFetch';
import { highlightJson } from '../utils/jsonUtils';
import { getMetadataSolanaDappStore } from '../utils/nftMetadataHelpers';
import {
  FileItem,
  MediaItem,
  MetadataMediaCarousel,
} from './MetadataMediaCarousel';

interface SolanaDappStoreExtension {
  media?: MediaItem[];
  files?: FileItem[];
}

interface MetadataJsonViewerProps {
  uri: string | null | undefined;
  className?: string;
}

export function MetadataJsonViewer({
  uri,
  className = '',
}: MetadataJsonViewerProps) {
  const { data, isLoading, error, refetch } = useMetadataFetch(uri);
  const [isExpanded, setIsExpanded] = useState(false);
  const solanaDappStore =
    getMetadataSolanaDappStore<SolanaDappStoreExtension>(data);

  if (!uri) {
    return null;
  }

  const handleCopy = () => {
    if (data) {
      navigator.clipboard.writeText(JSON.stringify(data, null, 2));
      toast.success('Metadata copied to clipboard');
    }
  };

  const handleOpenInNewTab = () => {
    if (uri) {
      window.open(uri, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div className={className}>
      <div className="mb-2 flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          aria-expanded={isExpanded}
          className="inline-flex items-center gap-1.5 rounded-lg border border-white/5 bg-white/[0.04] px-2.5 py-1.5 text-xs font-medium text-white/90 transition-colors hover:bg-white/[0.08]"
        >
          {isExpanded ? (
            <ExpandLessIcon
              aria-hidden="true"
              className="size-3.5 fill-current"
            />
          ) : (
            <ExpandMoreIcon
              aria-hidden="true"
              className="size-3.5 fill-current"
            />
          )}
          Metadata JSON
        </button>
        <div className="flex items-center gap-0.5">
          <button
            type="button"
            onClick={handleOpenInNewTab}
            className="rounded p-1 transition-colors hover:bg-white/10"
            title="Open in new tab"
          >
            <OpenInNewIcon className="size-3.5 fill-neutral-400" />
          </button>
          {!!data && (
            <button
              type="button"
              onClick={handleCopy}
              className="rounded p-1 transition-colors hover:bg-white/10"
              title="Copy JSON"
            >
              <ContentCopyIcon className="size-3.5 fill-neutral-400" />
            </button>
          )}
          {!!error && (
            <button
              type="button"
              onClick={refetch}
              className="rounded p-1 transition-colors hover:bg-white/10"
              title="Retry"
            >
              <RefreshIcon className="size-3.5 fill-neutral-400" />
            </button>
          )}
        </div>
      </div>

      {isExpanded && (
        <div className="rounded-xl border border-white/5 bg-black/20 p-3">
          {isLoading && (
            <div className="py-3 text-center text-xs text-neutral-500">
              Loading metadata…
            </div>
          )}

          {!!error && (
            <div className="text-xs text-rose-300">Error: {String(error)}</div>
          )}

          {!!data && (
            <div className="max-h-64 overflow-auto font-mono text-xs">
              <pre className="whitespace-pre text-neutral-300">
                <code
                  dangerouslySetInnerHTML={{
                    __html: highlightJson(JSON.stringify(data, null, 2)),
                  }}
                />
              </pre>
            </div>
          )}
        </div>
      )}

      {!!data && (solanaDappStore?.media || solanaDappStore?.files) && (
        <div className="mt-5">
          <h3 className="mb-3 text-[10.5px] font-medium uppercase tracking-[0.08em] text-neutral-500">
            NFT files &amp; media
          </h3>
          <MetadataMediaCarousel
            media={solanaDappStore?.media}
            files={solanaDappStore?.files}
          />
        </div>
      )}
    </div>
  );
}
